// src/SubscriptionSystem.js
export const SUBSCRIPTION_PLANS = {
  // REVIEW PLANS (for unlimited reviews)
  REVIEW_WEEKLY: {
    price: 799, // $7.99 in cents
    duration: 7,
    features: ['Unlimited book reviews', 'Earn coins for reviews', 'No 3-review limit', 'Priority support']
  },
  REVIEW_MONTHLY: {
    price: 2599, // $25.99 in cents
    duration: 30,
    features: ['Unlimited book reviews', 'Earn coins for reviews', 'No 3-review limit', 'Priority support', 'Save 25%']
  },
  REVIEW_YEARLY: {
    price: 24099, // $240.99 in cents
    duration: 365,
    features: ['Unlimited book reviews', 'Earn coins for reviews', 'No 3-review limit', 'Priority support', 'Save 42%!']
  }
};

export const canSubmitReview = (user) => {
  if (user.isAdmin) return { allowed: true };

  // Active subscription
  if (
    (user.reviewSubscription?.active && new Date(user.reviewSubscription.expiresAt) > new Date()) ||
    (user.subscription?.active && new Date(user.subscription.expiresAt) > new Date())
  ) {
    return { allowed: true };
  }

  // ✅ FIXED: Added fallback for users created before trialStartDate was added
  const trialStartDate = new Date(user.trialStartDate || user.createdAt || Date.now());
  const daysSinceStart = Math.floor(
    (Date.now() - trialStartDate) / (1000 * 60 * 60 * 24)
  );

  // ❌ Trial expired (5 days)
  if (daysSinceStart >= 5) {
    return { allowed: false, reason: 'Your 5-day free trial has ended. Subscribe to continue.' };
  }

  // ❌ Review limit reached
  if ((user.reviewsSubmitted || 0) >= 3) {
    return { allowed: false, reason: 'Free trial limit reached (3 reviews). Subscribe to continue.' };
  }

  return {
    allowed: true,
    remainingReviews: 3 - (user.reviewsSubmitted || 0),
    daysLeft: 5 - daysSinceStart
  };
};

// Initialize new user with trial
export const initializeNewUser = (userData) => {
  return {
    ...userData,
    coins: 50, // ✅ FIXED: Changed from 20 to 50 (matches welcome message)
    subscription: { plan: 'free', active: false, expiresAt: null },
    reviewSubscription: { plan: 'free', active: false, expiresAt: null },
    trialUsed: false,
    reviewsSubmitted: 0,
    trialStartDate: new Date().toISOString()
  };
};


// ============================================
// ADD to src/SubscriptionSystem.js
// The single gate that governs trial + subscription limits
// ============================================

export const TRIAL_LIMIT = 3;           // free activities for new users (one-time)
export const WEEKLY_LIMIT = 10;         // activities per week for subscribers

// Returns whether the user can perform an activity (claim/submit/upload)
// and info for messaging.
export const canDoActivity = (user) => {
  if (!user) return { allowed: false, reason: 'Not logged in' };
  if (user.isAdmin) return { allowed: true, unlimited: true };

  // Does the user have an active review subscription?
  const hasReviewSub =
    (user.reviewSubscription?.active && new Date(user.reviewSubscription.expiresAt) > new Date()) ||
    (user.subscription?.active && new Date(user.subscription.expiresAt) > new Date());

  if (hasReviewSub) {
    // Subscriber: 10 per week, resets weekly
    const now = new Date();
    const resetDate = user.weeklyActivitiesResetDate
      ? new Date(user.weeklyActivitiesResetDate)
      : null;

    // If a week has passed, they're effectively reset (actual reset written elsewhere)
    const weekPassed = !resetDate || (now - resetDate > 7 * 24 * 60 * 60 * 1000);
    const used = weekPassed ? 0 : (user.weeklyActivitiesUsed || 0);

    if (used < WEEKLY_LIMIT) {
      return {
        allowed: true,
        mode: 'subscription',
        remaining: WEEKLY_LIMIT - used
      };
    }
    // Weekly limit hit
    const daysLeft = resetDate
      ? Math.ceil((7 * 24 * 60 * 60 * 1000 - (now - resetDate)) / (24 * 60 * 60 * 1000))
      : 7;
    return {
      allowed: false,
      mode: 'subscription',
      reason: `You've used all ${WEEKLY_LIMIT} activities this week. Resets in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}.`
    };
  }

  // No subscription: on the one-time free trial
  const used = user.activitiesUsed || 0;
  if (used < TRIAL_LIMIT) {
    return {
      allowed: true,
      mode: 'trial',
      remaining: TRIAL_LIMIT - used
    };
  }

  // Trial exhausted
  return {
    allowed: false,
    mode: 'trial',
    trialOver: true,
    reason: `Your free trial (${TRIAL_LIMIT} activities) has ended. Subscribe to continue claiming books, submitting reviews, and keeping your books visible.`
  };
};

// Is this user currently gated (trial over, no sub)? Used to hide their books.
export const isUserGated = (user) => {
  if (!user) return false;
  if (user.isAdmin) return false;
  const result = canDoActivity(user);
  return !result.allowed && result.trialOver === true;
};